import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Section, StateView, formatBytes, useAuth } from "@ozpanel/ui";
import { StatusPill, type StatusTone } from "./PageHeader";

// Bandwidth — ONE component set for both consoles.
//
// The figure is web + published ports + mail, in + out, month to date, across every node
// (usage/bandwidth.go). It is never shown without whether it is COMPLETE: each node reports, per
// source, whether it measured on its last pass, and a node or source that is off, failing, stale or
// has never reported is named beside the number. "0 GB" from a node that is not counting must never
// read as "no traffic" — that is exactly what happened for three weeks before this existed.

export type SourceState = "counting" | "off" | "error" | "stale" | "never";

export interface SourceStatus {
  state: SourceState;
  detail?: string;
  measured_at?: string;
}

export interface BandwidthCoverage {
  counting: boolean;
  nodes: { node: string; sources: Record<string, SourceStatus> }[] | null;
  not_counted: string[] | null;
  counts?: string;
  excludes?: string;
}

const SOURCES: { key: string; label: string; sub: string }[] = [
  { key: "web", label: "Web", sub: "Caddy HTTP metrics" },
  { key: "ports", label: "Ports", sub: "nftables counters" },
  { key: "mail", label: "Mail", sub: "Postfix journal" },
];

const stateTone: Record<string, StatusTone> = { counting: "ok", off: "warn", error: "bad", stale: "warn", never: "neutral" };
const stateWord = (src: string, s: SourceState) =>
  s === "counting" ? "counting" : s === "off" ? (src === "web" ? "metrics off" : "off") : s === "never" ? "never reported" : s;

export function useBandwidthCoverage() {
  const { client } = useAuth();
  return useQuery({
    queryKey: ["bandwidth-coverage"],
    queryFn: () => client.apiFetch<BandwidthCoverage>("/stats/bandwidth/coverage"),
    retry: false,
    refetchInterval: 60000,
  });
}

/** Per node, per source: is it being measured? The web row IS "are Caddy's metrics in force". */
export function BandwidthCoverageMatrix({ coverage }: { coverage?: BandwidthCoverage }) {
  const nodes = coverage?.nodes ?? [];
  return (
    <div className="text-[12px]">
      <table className="w-full text-left">
        <thead className="text-muted">
          <tr>
            <th className="py-1 pr-3 font-normal">Node</th>
            {SOURCES.map((s) => (
              <th key={s.key} className="py-1 pr-3 font-normal">
                {s.label} <span className="text-[11px]">· {s.sub}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {nodes.map((n) => (
            <tr key={n.node} className="border-t border-line align-top">
              <td className="py-1.5 pr-3 font-mono">{n.node}</td>
              {SOURCES.map((s) => {
                const st = n.sources[s.key] ?? { state: "never" as SourceState };
                return (
                  <td key={s.key} className="py-1.5 pr-3" title={st.detail}>
                    <StatusPill tone={stateTone[st.state] ?? "neutral"}>{stateWord(s.key, st.state)}</StatusPill>
                    {st.state !== "counting" && st.detail && (
                      <div className="mt-1 max-w-[280px] text-[11px] leading-snug text-muted">{st.detail}</div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {coverage?.counts && <p className="mt-2 leading-relaxed text-muted"><b className="text-fg">Counted:</b> {coverage.counts}</p>}
      {coverage?.excludes && <p className="mt-1 leading-relaxed text-muted"><b className="text-fg">Not counted anywhere:</b> {coverage.excludes}</p>}
    </div>
  );
}

/** One line: is bandwidth complete right now? */
export function BandwidthCountingPill({ coverage }: { coverage?: BandwidthCoverage }) {
  if (!coverage) return <StatusPill tone="neutral">bandwidth: unknown</StatusPill>;
  return coverage.counting ? (
    <StatusPill tone="ok">bandwidth counted on every node</StatusPill>
  ) : (
    <StatusPill tone="warn">bandwidth incomplete</StatusPill>
  );
}

/** A site's web traffic is counted when Caddy's metrics are in force on every node that serves it. */
export function SiteBandwidthPill({ coverage }: { coverage?: BandwidthCoverage }) {
  if (!coverage) return null;
  const missing = (coverage.nodes ?? []).filter((n) => n.sources.web?.state !== "counting").map((n) => n.node);
  if ((coverage.nodes ?? []).length === 0) return <StatusPill tone="neutral">traffic: not measured</StatusPill>;
  return missing.length === 0 ? (
    <StatusPill tone="ok">traffic counted</StatusPill>
  ) : (
    <span title={(coverage.nodes ?? []).filter((n) => missing.includes(n.node)).map((n) => `${n.node}: ${n.sources.web?.detail ?? ""}`).join("\n")}>
      <StatusPill tone="warn">traffic not counted on {missing.join(", ")}</StatusPill>
    </span>
  );
}

interface UsageForBandwidth {
  current?: { metrics?: Record<string, number> };
  history?: Record<string, ({ date: string; value: number } & Record<string, number | string>)[] | null>;
  against_limits?: { key: string; limit: number; used: number; unit?: string; percent: number; incomplete?: string[] | null }[] | null;
  bandwidth_coverage?: BandwidthCoverage;
}

const PARTS: { key: string; label: string }[] = [
  { key: "web_out_bytes", label: "Web out" },
  { key: "web_in_bytes", label: "Web in" },
  { key: "port_out_bytes", label: "Ports out" },
  { key: "port_in_bytes", label: "Ports in" },
  { key: "mail_out_bytes", label: "Mail sent" },
  { key: "mail_in_bytes", label: "Mail received" },
];

/** An account's bandwidth: month-to-date total against its limit, the six parts, 30 days, coverage. */
export function BandwidthPanel({ tenantId, className }: { tenantId?: number | string; className?: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["tenant-usage", String(tenantId)],
    enabled: tenantId != null,
    queryFn: () => client.apiFetch<UsageForBandwidth>(`/tenants/${tenantId}/usage`),
    retry: false,
  });
  const m = q.data?.current?.metrics ?? {};
  const cov = q.data?.bandwidth_coverage;
  const lim = (q.data?.against_limits ?? []).find((r) => r.key === "bandwidth_gb_month");
  const days = (q.data?.history?.bandwidth_bytes ?? []).slice(-30);
  const peak = Math.max(1, ...days.map((d) => Number(d.value) || 0));
  const total = m.bandwidth_bytes;

  return (
    <Section title="Bandwidth this month" className={className} actions={<BandwidthCountingPill coverage={cov} />}>
      <StateView loading={q.isLoading} error={q.error} errorContext="bandwidth">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-[22px] font-semibold">{total != null ? formatBytes(total) : "—"}</span>
            <span className="text-[12.5px] text-muted">
              {lim && lim.limit > 0 ? `of ${lim.limit} ${lim.unit ?? "GB/mo"} · ${Math.round(lim.percent)}%` : "no limit set"}
              {" · "}in + out, web, ports and mail, every node
            </span>
          </div>
          {cov && !cov.counting && (
            <div className="rounded-lg border border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-3 text-[12px] leading-relaxed">
              <b>This figure is incomplete.</b> Not being measured right now:
              <ul className="mt-1 list-disc pl-5 text-muted">
                {(cov.not_counted ?? []).map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {PARTS.map((p) => (
              <div key={p.key} className="rounded-lg border border-line p-2.5">
                <div className="text-[11px] text-muted">{p.label}</div>
                <div className="font-mono text-[13px]">{m[p.key] != null ? formatBytes(m[p.key]!) : "—"}</div>
              </div>
            ))}
          </div>
          {days.length > 0 && (
            <div>
              <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">Per day</div>
              <div className="flex h-20 items-end gap-[3px]">
                {days.map((d) => (
                  <div
                    key={d.date}
                    className="min-w-[4px] flex-1 rounded-t bg-accent/70"
                    style={{ height: `${Math.max(2, ((Number(d.value) || 0) / peak) * 100)}%` }}
                    title={`${d.date}: ${formatBytes(Number(d.value) || 0)}\n${PARTS.map((p) => `${p.label} ${formatBytes(Number(d[p.key]) || 0)}`).join("\n")}`}
                  />
                ))}
              </div>
            </div>
          )}
          <details>
            <summary className="cursor-pointer text-[12px] text-muted hover:text-fg">What is measured, on which node</summary>
            <div className="mt-2">
              <BandwidthCoverageMatrix coverage={cov} />
            </div>
          </details>
        </div>
      </StateView>
    </Section>
  );
}
